import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PostStory } from 'components-ref/molecules'
import { usePreviewContext } from 'context/previewContext'
import { usePreviewStore } from 'store/previewStore'

import { type IProps } from './StoryPostWrapper.types'

export const StoryPostWrapper = ({
    postData,
    postIndex,
    dataQuery,
    toggleStats
}: IProps) => {
    const [activeStorySlide, setActiveStorySlide] = useState<number | null>(null)
    const previewData = usePreviewContext()
    const fullscreenHandoffRef = useRef(false)
    const previewOpen = usePreviewStore(state => state.previewOpen)
    const wasPreviewOpenRef = useRef(previewOpen)

    useEffect(() => {
        if (wasPreviewOpenRef.current && !previewOpen) {
            fullscreenHandoffRef.current = false
        }

        wasPreviewOpenRef.current = previewOpen
    }, [previewOpen])

    // Stable reversed stories (only changes when stories array identity changes)
    // Sort stories newest first without mutating the original array.
    const sortedStories = useMemo(() => {
        return [...(postData.stories ?? [])].sort(
            (a, b) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
        )
    }, [postData.stories])
    //  Memoize initialSlide so it’s not recomputed every render
    const initialSlide = useMemo(() => {
        const idx = sortedStories.findIndex(story => !story.seen)
        return idx !== -1 ? idx : 0
    }, [sortedStories])

    //  Stable click handler (prevents needless prop churn downstream)
    const handleWrapperClick = useCallback(() => {
        fullscreenHandoffRef.current = true

        if (previewData.minimized) {
            previewData.updateSelectedPost(
                postIndex,
                activeStorySlide ?? initialSlide,
                'story',
                postData,
                dataQuery
            )
            previewData.handleMinimize(false)
        } else {
            previewData.addModal(
                postIndex,
                activeStorySlide ?? initialSlide,
                'story',
                postData,
                dataQuery
            )
        }
    }, [
        previewData,
        postIndex,
        activeStorySlide,
        initialSlide,
        postData,
        dataQuery
    ])
    //  These are frequently recreated objects in some codebases — memo for safety
    const avatarUrl = postData.user?.avatar?.url ?? null
    const author = postData.user?.display_name ?? ''
    const statsAmount = postData.overall_revenue

    return (
        <div onClick={handleWrapperClick}>
            <PostStory
                postId={postData.id}
                storyData={sortedStories}
                dataQuery={dataQuery}
                initialSlide={initialSlide}
                avatarUrl={avatarUrl}
                author={author}
                statsAmount={statsAmount}
                setActiveStorySlide={setActiveStorySlide}
                fullscreenHandoffRef={fullscreenHandoffRef}
                toggleStats={toggleStats}
                
            />
        </div>
    )
}
